import { NgClass } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { VetAIHeaderComponent } from 'src/shared/components/design/vetai-header/vetai-header.component';
import { VetAIHeader, VetAIHeaderItemType } from 'src/shared/components/design/vetai-header/vetai-header.interface';
import { StaffOnlineWriteComponent } from 'src/shared/components/staffonline/staffonlinewrite/staffonlinewrite.component';
import { AuthStateService } from 'src/shared/services/auth-state/auth-state.service';
import { DarkModeService } from 'src/shared/services/darkmode/darkmode_service';
import { initTranslations } from 'src/shared/translate/custom.translate.loader';
import { environment } from '../environments/environment';
import { StaffProfileStore } from './stores/staff-profile/staff-profile.store';
import { getPmsDashboardPath } from './utils/pms-dashboard-path.util';

@Component({
  selector: 'app-root',
  templateUrl: './pms.component.html',
  providers: [StaffProfileStore],
  imports: [RouterOutlet, VetAIHeaderComponent, StaffOnlineWriteComponent, NgClass],
})
export class PMSComponent {
  darkModeService: DarkModeService = inject(DarkModeService);
  authStateService: AuthStateService = inject(AuthStateService);
  menu: VetAIHeader;
  private lastReportedDeauthReason: string | null = null;

  authChangedEffect = effect(() => {
    const deauthReason = this.authStateService.deauthReason();
    const deauthedStaffUser = this.authStateService.deauthedStaffUser();
    const isAuthReady = this.authStateService.isAuthReady();

    if (isAuthReady) {
      if (deauthReason) {
        if (this.lastReportedDeauthReason !== deauthReason) {
          Sentry.captureMessage('Staff authentication revoked', {
            level: 'warning',
            tags: {
              'staff-deauth': 'true',
              deauth_reason: deauthReason,
            },
            user: deauthedStaffUser
              ? {
                  id: deauthedStaffUser.id,
                  email: deauthedStaffUser.email ?? undefined,
                  username: deauthedStaffUser.username ?? undefined,
                }
              : undefined,
          });
          this.lastReportedDeauthReason = deauthReason;
          this.authStateService.clearDeauthReason();
        }
      } else {
        this.lastReportedDeauthReason = null;
      }
    }
  });

  constructor() {
    initTranslations();

    this.menu = {
      hiddenpaths: [
        '/login',
        '/register',
        '/unauthorised',
        '/forgottenpassword',
        '/notfound',
        '/checkaccess',
        '/consultation/',
        '/onboarding/staff',
        '/aiReview',
        '/trial-consultation',
        '/not-clinician',
      ],
      logo: 'https://firebasestorage.googleapis.com/v0/b/vetai-e3e47.appspot.com/o/joii%2Fvetaiicon.png?alt=media&token=3e3942bd-4fd8-48c6-9249-096d5b35bef9',
      accountpath: '/account/staffprofile',
      items: [
        {
          id: getPmsDashboardPath(),
          title: 'Home',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: 'Home',
          dataautomationid: 'pms-sidebar-dashboard',
          hidden: false,
        },
        {
          id: '/history',
          title: 'History',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: 'History',
          dataautomationid: 'pms-sidebar-consultation-history',
          hidden: false,
        },
        {
          id: '/calendar',
          title: 'Calendar',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: 'Calendar',
          dataautomationid: 'pms-sidebar-calendar',
          hidden: false,
        },
        {
          id: '/account/staffprofile',
          title: 'Profile',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: 'UserIcon',
          dataautomationid: 'pms-sidebar-profile',
          hidden: false,
        },
        {
          id: '/videocheck',
          title: 'Test Video',
          type: VetAIHeaderItemType.Path,
          requiredroles: [],
          icon: 'VideoIcon',
          dataautomationid: 'pms-sidebar-test-video',
          hidden: false,
        },
        ...(environment.production
          ? []
          : [
              {
                id: '/testPhone',
                title: 'Test Phone',
                type: VetAIHeaderItemType.Path,
                requiredroles: [],
                icon: 'PhoneIcon',
                dataautomationid: 'pms-sidebar-test-phone',
                hidden: false,
              },
            ]),
        {
          id: 'token-copier',
          title: 'Copy Auth',
          type: VetAIHeaderItemType.TokenCopier,
          requiredroles: [],
          icon: 'Clipboard',
          dataautomationid: 'pms-sidebar-token-copier',
          hidden: false,
        },
      ],
    };

    if (environment.production) {
      this.loadHotjar();
    }
  }

  private loadHotjar() {
    try {
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.innerHTML = ` (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:5337804,hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
      document.head.appendChild(s);
    } catch {
      console.error('Failed to load Hotjar');
    }
  }
}
